import {
  generalPractitionerUpdateRequestSchema,
  type PatientGeneralPractitionerUpdateRequest,
} from '@quromedical/models';
import { CrudForm, type SubmissionResult } from 'components/form';
import type { Field, PrimitiveValueType, SelectOption } from 'components/types';
import type { AsyncFetcher } from 'design-system';
import type { FormikErrors } from 'formik';
import type React from 'react';
import { useCallback, useMemo } from 'react';
import { strings } from 'strings';

export type CareTeamOrPractitionerFetcher = AsyncFetcher<
  PrimitiveValueType,
  any
>;
export type TeamOrDepartmentOrganizationFetcher = AsyncFetcher<
  PrimitiveValueType,
  any
>;

interface CareTeamFormProps {
  initialPractitioners?: SelectOption[];
  initialOrganizations?: SelectOption[];
  initialValues?: PatientGeneralPractitionerUpdateRequest;
  showErrors?: boolean;
  onSubmit?: (
    data: PatientGeneralPractitionerUpdateRequest,
  ) => Promise<SubmissionResult<unknown>>;
  onValidChange?: (data?: PatientGeneralPractitionerUpdateRequest) => void;
  onChange?: (
    data: Partial<PatientGeneralPractitionerUpdateRequest>,
    errors: FormikErrors<PatientGeneralPractitionerUpdateRequest>,
    hasErrors: boolean,
  ) => void;
  onCancel?: () => void;
  careTeamOrPractitionerFetcher: CareTeamOrPractitionerFetcher;
  teamOrDepartmentOrganizationFetcher: TeamOrDepartmentOrganizationFetcher;
}

const createFields = (
  careTeamOrPractitionerFetcher: CareTeamOrPractitionerFetcher,
  teamOrDepartmentOrganizationFetcher: TeamOrDepartmentOrganizationFetcher,
  initialPractitioners?: SelectOption[],
  initialOrganizations?: SelectOption[],
): Field<PatientGeneralPractitionerUpdateRequest>[] => [
  {
    subfields: [
      {
        key: 'practitioners',
        label: strings.CardTitleCareTeamPractitioner,
        type: 'combobox-multiple',
        searchable: true,
        fetcher: careTeamOrPractitionerFetcher,
        initialSelection: initialPractitioners,
      },
      {
        key: 'organizations',
        label: strings.FormLabelAssignedOrgOrTeams,
        type: 'combobox-multiple',
        searchable: true,
        fetcher: teamOrDepartmentOrganizationFetcher,
        initialSelection: initialOrganizations,
      },
    ],
  },
];

export const CareTeamForm: React.FC<CareTeamFormProps> = ({
  initialOrganizations,
  initialPractitioners,
  initialValues = {},
  showErrors,
  onValidChange,
  onSubmit,
  onChange,
  onCancel,
  careTeamOrPractitionerFetcher,
  teamOrDepartmentOrganizationFetcher,
}) => {
  const fields = useMemo(
    () =>
      createFields(
        careTeamOrPractitionerFetcher,
        teamOrDepartmentOrganizationFetcher,
        initialPractitioners,
        initialOrganizations,
      ),
    [
      careTeamOrPractitionerFetcher,
      teamOrDepartmentOrganizationFetcher,
      initialPractitioners,
      initialOrganizations,
    ],
  );

  const handleChange = useCallback(
    (
      data: Partial<PatientGeneralPractitionerUpdateRequest>,
      errors: FormikErrors<PatientGeneralPractitionerUpdateRequest>,
      hasErrors: boolean,
    ) => {
      if (onChange) {
        onChange(data, errors, hasErrors);
      }

      if (onValidChange) {
        if (hasErrors) {
          onValidChange(undefined);
        } else {
          onValidChange(data as PatientGeneralPractitionerUpdateRequest);
        }
      }
    },
    [onChange, onValidChange],
  );
  return (
    <CrudForm<PatientGeneralPractitionerUpdateRequest>
      title={strings.CardTitleCareTeam}
      validationSchema={generalPractitionerUpdateRequestSchema}
      fields={fields}
      initialValues={initialValues}
      onSubmit={onSubmit}
      onChange={handleChange}
      onChangeDebounceTime={onChange ? 0 : undefined}
      onSecondarySubmit={onCancel}
      showSecondarySubmitButton={!!onCancel}
      showSubmitButton={!!onSubmit}
      showErrors={showErrors}
    />
  );
};
